import { Navigate, Outlet, Route, Routes, useNavigate } from "react-router-dom";

import { useAuth } from "./context/AuthContext";
import { StoreProvider } from "./context/StoreContext";
import { LogoMark } from "./components/branding";
import { Spinner } from "./components/ui";
import App from "./App";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import CalendarPage from "./pages/CalendarPage";
import DocumentsPage from "./pages/DocumentsPage";
import AccountsPage from "./pages/AccountsPage";
import PromptPage from "./pages/PromptPage";
import SettingsPage from "./pages/SettingsPage";
import StoryboardPage from "./pages/StoryboardPage";
import UgcPage from "./pages/UgcPage";

function Splash() {
  return (
    <div className="min-h-[100dvh] bg-black flex flex-col items-center justify-center gap-4">
      <div className="w-10 h-10 rounded-xl bg-white text-black flex items-center justify-center">
        <LogoMark size={22} />
      </div>
      <Spinner size={20} className="text-white/60" />
    </div>
  );
}

// Public landing. Signed in users skip straight to the app.
function LandingGate() {
  const { ready, isAuthed } = useAuth();
  const navigate = useNavigate();
  if (!ready) return <Splash />;
  if (isAuthed) return <Navigate to="/calendar" replace />;
  return <LandingPage onGetStarted={() => navigate("/auth")} />;
}

// Public auth page. Signed in users skip straight to the app.
function AuthGate() {
  const { ready, isAuthed } = useAuth();
  const navigate = useNavigate();
  if (!ready) return <Splash />;
  if (isAuthed) return <Navigate to="/calendar" replace />;
  return <AuthPage onBack={() => navigate("/")} />;
}

// Everything inside the app requires a session. Signing out drops isAuthed,
// which lands the user back on the auth page.
function RequireAuth() {
  const { ready, isAuthed } = useAuth();
  if (!ready) return <Splash />;
  if (!isAuthed) return <Navigate to="/auth" replace />;
  return (
    <StoreProvider>
      <App>
        <Outlet />
      </App>
    </StoreProvider>
  );
}

export default function Root() {
  return (
    <Routes>
      <Route path="/" element={<LandingGate />} />
      <Route path="/auth" element={<AuthGate />} />
      <Route element={<RequireAuth />}>
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/prompt" element={<PromptPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/storyboard/:id" element={<StoryboardPage />} />
        <Route path="/ugc/:id" element={<UgcPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}